import { trackUpgradeView } from "../lib/metaIntentTracking";
import { internalReturnPath } from "../lib/onboardingIntent";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Loader2,
  Shield,
  Star,
} from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CreditPackOffers } from "../components/CreditPackOffers";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  BUSINESS_CONTACT_URL,
  BUSINESS_FEATURES,
  CONTEXTUAL_MESSAGES,
  DEFAULT_MESSAGE,
  FAQ_ITEMS,
  SOCIAL_PROOF_AVATARS,
  TESTIMONIALS,
} from "../constants/upgrade-data";
import { useStripe } from "../contexts/StripeContext";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckoutContinuation,
  isCheckoutContinuationUsable,
  useCheckoutContinuation,
} from "../hooks/useCheckoutContinuation";
import { cn } from "../lib/utils";

type BillingInterval = "month" | "year";

type TierComparablePlan = {
  tier?: string;
  name?: string;
  video_credits?: number;
};

const PLAN_TIER_RANK: Record<string, number> = {
  lite: 0,
  starter: 1,
  growth: 2,
  pro: 3,
};

const getPlanTierRank = (plan: TierComparablePlan): number | undefined =>
  PLAN_TIER_RANK[(plan.tier || plan.name || "").trim().toLowerCase()];

/** Positive when `plan` is a higher tier than `current`, 0 for the same tier. */
function comparePlanTier(
  plan: TierComparablePlan,
  current: TierComparablePlan | null,
): number {
  if (!current) return 1;
  const rank = getPlanTierRank(plan);
  const currentRank = getPlanTierRank(current);
  if (rank !== undefined && currentRank !== undefined) {
    return rank - currentRank;
  }
  // Legacy prices without a recognizable tier: the monthly credit allowance
  // rises with tier and is the same for monthly and yearly billing.
  return (plan.video_credits ?? 0) - (current.video_credits ?? 0);
}

export default function UpgradePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const from = internalReturnPath((location.state as any)?.from) || internalReturnPath(searchParams.get("backTo")) || "/generate";
  const reason = searchParams.get("reason") || undefined;
  const customMessage = searchParams.get("message") || undefined;
  const successUrl = searchParams.get("successUrl") || (searchParams.has("backTo") ? new URL(from, window.location.origin).href : undefined);
  // Presence of a ?promo param turns on Stripe's promo-code field at checkout.
  const allowPromo = !!searchParams.get("promo");

  const { customerData, currentUser, activeWorkspaceId } = useAuth();
  const selectedPlanStarted = useRef(false);
  const [entryCheckoutError, setEntryCheckoutError] = useState("");
  const {
    availableCreditPacks,
    availablePlans,
    createCreditCheckoutSession,
    createCheckoutSession,
    resumeCheckoutSession,
    isLoading,
    currentPlan,
  } = useStripe();
  // Monthly first: since the Sep 30 reprice every $480 yearly checkout
  // expired, and the one payer abandoned yearly and paid monthly a minute later.
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("month");
  const [startingCheckoutPriceId, setStartingCheckoutPriceId] = useState<
    string | null
  >(null);
  const checkoutStartInFlightRef = useRef(false);
  const appliedCanceledPlanIntervalRef = useRef<string | undefined>(undefined);
  const isStartingCheckout = startingCheckoutPriceId !== null;

  // Track page view
  useEffect(() => {
    posthog.capture("upgrade_page_viewed", { reason });
  }, [reason]);

  const isYearly = billingInterval === "year";
  const canceledCheckout = searchParams.get("canceled") === "true";
  const canceledPriceId = searchParams.get("priceId") || undefined;
  const checkoutContinuation = useCheckoutContinuation(customerData);
  const activeCheckoutContinuation =
    !canceledCheckout && !isStartingCheckout ? checkoutContinuation : undefined;
  const videoCredits =
    (customerData?.videoCredits ?? 0) + (customerData?.bonusCredits ?? 0);
  const hasActiveSubscription = !!currentPlan?.stripePriceId;
  const isOutOfCreditsContext =
    hasActiveSubscription &&
    (videoCredits === 0 ||
      reason === "no_credits" ||
      reason === "batch_create_insufficient_credits");
  const showCreditPacks =
    isOutOfCreditsContext && availableCreditPacks.length > 0;

  useEffect(() => {
    if (!currentUser || !activeWorkspaceId || isLoading || !availablePlans.length) return;
    let tracked = false;
    const track = () => {
      if (!tracked && document.visibilityState === "visible") {
        tracked = true;
        void trackUpgradeView(currentUser, activeWorkspaceId);
      }
    };
    track();
    document.addEventListener("visibilitychange", track);
    return () => document.removeEventListener("visibilitychange", track);
  }, [currentUser, activeWorkspaceId, isLoading, availablePlans.length]);

  const getDisplayPrice = (plan: any) => {
    if (plan.interval === "year") {
      return Math.round((plan.price / 12) * 100) / 100;
    }
    return plan.price;
  };

  const getYearlyTotal = (plan: any) => {
    return plan.interval === "year" ? plan.price : plan.price * 12;
  };

  // Archived prices still bill existing subscribers, so the subscription's own
  // plan detail (not the catalog) says which tier they are on.
  const currentPlanDetail = hasActiveSubscription
    ? currentPlan?.planDetail ?? null
    : null;

  // A yearly subscriber still lands on yearly billing, where their plan is.
  const appliedSubscriberIntervalRef = useRef(false);
  useEffect(() => {
    if (appliedSubscriberIntervalRef.current || currentPlanDetail?.interval !== "year") return;
    appliedSubscriberIntervalRef.current = true;
    setBillingInterval("year");
  }, [currentPlanDetail?.interval]);

  const canceledPlan = useMemo(() => {
    if (!canceledPriceId) return undefined;
    return availablePlans.find((plan) => plan.id === canceledPriceId);
  }, [availablePlans, canceledPriceId]);

  const canceledCreditPack = useMemo(() => {
    if (!canceledPriceId) return undefined;
    return availableCreditPacks.find((pack) => pack.id === canceledPriceId);
  }, [availableCreditPacks, canceledPriceId]);

  const continuationPlan = useMemo(() => {
    if (!activeCheckoutContinuation?.priceId) return undefined;
    return availablePlans.find(
      (plan) => plan.id === activeCheckoutContinuation.priceId,
    );
  }, [activeCheckoutContinuation?.priceId, availablePlans]);

  const continuationCreditPack = useMemo(() => {
    if (!activeCheckoutContinuation?.priceId) return undefined;
    return availableCreditPacks.find(
      (pack) => pack.id === activeCheckoutContinuation.priceId,
    );
  }, [activeCheckoutContinuation?.priceId, availableCreditPacks]);

  useEffect(() => {
    if (!canceledPlan?.id) {
      appliedCanceledPlanIntervalRef.current = undefined;
      return;
    }

    if (appliedCanceledPlanIntervalRef.current === canceledPlan.id) return;

    appliedCanceledPlanIntervalRef.current = canceledPlan.id;
    setBillingInterval(canceledPlan.interval);
  }, [canceledPlan?.id, canceledPlan?.interval]);

  // Tier, not price, decides what is an upgrade. Yearly billing makes a tier
  // cheaper per month, so comparing prices offered yearly subscribers their own
  // tier on monthly billing as an "upgrade" (and hid higher tiers billed yearly).
  const availablePlansForUpgrade = useMemo(
    () =>
      availablePlans
        // The catalog includes the $0 Free price; it is never something to buy.
        .filter((plan) => plan.price > 0 && plan.interval === billingInterval)
        .filter((plan) => comparePlanTier(plan, currentPlanDetail) >= 0)
        .sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b)),
    [availablePlans, billingInterval, currentPlanDetail],
  );

  const handleChoosePlan = async (plan: any) => {
    posthog.capture("upgrade_plan_selected", {
      plan_name: plan.name,
      plan_id: plan.id,
      plan_interval: plan.interval,
      reason,
    });

    await createCheckoutSession(plan.id, successUrl, allowPromo);
  };

  const startFreshCheckoutFromContinuation = async (
    continuation: CheckoutContinuation,
  ) => {
    if (!continuation.priceId) return;

    posthog.capture("upgrade_checkout_fresh_session_started", {
      checkout_session_id: continuation.sessionId,
      checkout_state: continuation.kind,
      price_id: continuation.priceId,
      item_type:
        continuation.mode === "subscription" ? "subscription" : "credit_pack",
      reason,
    });

    if (continuation.mode === "subscription") {
      await createCheckoutSession(continuation.priceId, successUrl, allowPromo);
      return;
    }

    await createCreditCheckoutSession(continuation.priceId, successUrl);
  };

  const continueCheckout = async (
    continuation: CheckoutContinuation,
    surface: string,
  ) => {
    posthog.capture("upgrade_checkout_continued", {
      checkout_session_id: continuation.sessionId,
      checkout_state: continuation.kind,
      price_id: continuation.priceId,
      item_name:
        continuationPlan?.name ||
        continuationCreditPack?.name ||
        continuation.itemName,
      item_type:
        continuation.mode === "subscription" ? "subscription" : "credit_pack",
      surface,
      reason,
    });

    if (!isCheckoutContinuationUsable(continuation)) {
      await startFreshCheckoutFromContinuation(continuation);
      return;
    }

    try {
      if (continuation.kind === "recovery" && continuation.recoveryUrl) {
        window.location.href = continuation.recoveryUrl;
        return;
      }

      await resumeCheckoutSession(continuation.sessionId);
    } catch {
      await startFreshCheckoutFromContinuation(continuation);
    }
  };

  const handleResumeCheckout = async () => {
    const canceledItem = canceledPlan || canceledCreditPack;
    if (!canceledItem) return;

    posthog.capture("upgrade_checkout_resumed", {
      price_id: canceledItem.id,
      item_name: canceledItem.name,
      item_type: canceledPlan ? "subscription" : "credit_pack",
      reason,
    });

    if (
      checkoutContinuation &&
      checkoutContinuation.priceId === canceledItem.id
    ) {
      await continueCheckout(checkoutContinuation, "cancel_return");
      return;
    }

    if (canceledPlan) {
      await createCheckoutSession(canceledPlan.id, successUrl, allowPromo);
      return;
    }

    await createCreditCheckoutSession(canceledCreditPack!.id, successUrl);
  };

  const handleContinuationClick = async () => {
    if (!activeCheckoutContinuation) return;
    await continueCheckout(activeCheckoutContinuation, "upgrade_page");
  };

  const handlePlanCtaClick = async (plan: any, isHighlightedPlan: boolean) => {
    // React state is not synchronous, so use a ref as the immediate guard. This
    // prevents a second click in the same render frame from starting another
    // Checkout request while the disabled state is being painted.
    if (checkoutStartInFlightRef.current) return;
    checkoutStartInFlightRef.current = true;
    setStartingCheckoutPriceId(plan.id);

    try {
      if (
        isHighlightedPlan &&
        canceledCheckout &&
        canceledPriceId === plan.id
      ) {
        await handleResumeCheckout();
        return;
      }

      if (
        isHighlightedPlan &&
        activeCheckoutContinuation?.priceId === plan.id
      ) {
        await handleContinuationClick();
        return;
      }

      await handleChoosePlan(plan);
    } finally {
      checkoutStartInFlightRef.current = false;
      setStartingCheckoutPriceId(null);
    }
  };

  // Contextual heading
  const { title, subtitle } = reason
    ? CONTEXTUAL_MESSAGES[reason] || DEFAULT_MESSAGE
    : DEFAULT_MESSAGE;

  const displaySubtitle = customMessage || subtitle;
  const planCardCount = availablePlansForUpgrade.length + 1;
  const isSinglePlanCard = planCardCount === 1;
  const singlePlanCardClass =
    "sm:grid sm:grid-cols-[minmax(240px,0.85fr)_minmax(260px,1.15fr)] sm:items-center sm:gap-8";
  const singlePlanFeaturesClass =
    "sm:mt-0 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0";

  useEffect(() => {
    if (location.hash !== "#credit-packs" || !showCreditPacks) return;

    window.requestAnimationFrame(() => {
      document
        .getElementById("credit-packs")
        ?.scrollIntoView({ block: "start" });
    });
  }, [location.hash, showCreditPacks]);

  // Website plan selection uses the existing checkout action. Cancellation
  // deliberately omits planId, so Back never starts checkout again.
  useEffect(() => {
    const requested = searchParams.get("planId");
    if (!requested || isLoading || canceledCheckout || selectedPlanStarted.current || !availablePlans.length) return;
    const interval = requested.endsWith("_yearly") ? "year" : "month";
    const tier = requested.replace(/_yearly$/, "").toLowerCase();
    const plan = availablePlans.find(p => p.interval === interval && p.name.toLowerCase().includes(tier));
    selectedPlanStarted.current = true;
    if (!plan || !["starter", "growth", "pro", "lite"].includes(tier)) {
      setEntryCheckoutError("That plan is no longer available. Choose a plan below or go back to continue.");
      return;
    }
    setBillingInterval(interval);
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete("planId");
    setSearchParams(cleaned, { replace: true });
    setStartingCheckoutPriceId(plan.id);
    void createCheckoutSession(plan.id, successUrl, allowPromo)
      .catch(() => setEntryCheckoutError("Couldn’t open checkout. Choose your plan below to try again."))
      .finally(() => setStartingCheckoutPriceId(null));
  }, [searchParams, isLoading, canceledCheckout, availablePlans, createCheckoutSession, successUrl, allowPromo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back button — fixed top-left */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5">
        <button
          onClick={() => navigate(from || "/generate")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {entryCheckoutError && <p role="alert" className="max-w-6xl mx-auto px-6 pt-4 text-sm text-destructive">{entryCheckoutError}</p>}
      {/* ── HERO SECTION ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-4 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {title}
        </h1>
        <p className="mt-2 text-base text-slate-500 max-w-xl mx-auto">
          {displaySubtitle}
        </p>

        {!isOutOfCreditsContext && (
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <div className="flex items-center gap-2.5">
              <div className="flex -space-x-2.5">
                {SOCIAL_PROOF_AVATARS.map((avatar, i) => (
                  <img
                    key={i}
                    src={avatar.src}
                    alt={avatar.alt}
                    className="h-8 w-8 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div className="flex flex-col items-start">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <span className="text-xs text-slate-500 font-medium">
                  Trusted by 5,000+ users
                </span>
              </div>
            </div>

            <div className="hidden sm:block w-px h-8 bg-slate-200" />

            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
              <Shield className="h-3.5 w-3.5 text-emerald-500" />
              <span>7-day money back</span>
              <span className="text-slate-300 mx-1">|</span>
              <span>Cancel anytime</span>
            </div>
          </div>
        )}
      </div>

      {/* ── PLAN CARDS ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {showCreditPacks && (
          <section id="credit-packs" className="scroll-mt-8 mb-10">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-xl text-left">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  One-time credits
                </p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                  Add credits without changing plans
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Use a credit pack for this batch. If you run out most months,
                  compare plan upgrades below.
                </p>
              </div>
            </div>
            <CreditPackOffers
              successUrl={
                searchParams.get("successUrl") ||
                `${window.location.origin}/generate?credits=success&credit_session_id={CHECKOUT_SESSION_ID}`
              }
            />
          </section>
        )}

        <section
          id="monthly-options"
          className={cn(
            "scroll-mt-8",
            showCreditPacks ? "border-t border-slate-200 pt-10" : "pt-4",
          )}
        >
          <div
            className={cn(
              "mb-6 flex flex-col gap-4",
              showCreditPacks
                ? "sm:flex-row sm:items-end sm:justify-between"
                : "items-center",
            )}
          >
            {showCreditPacks && (
              <div className="max-w-xl text-left">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  More credits every month
                </p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                  Upgrade if this is your new pace
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Plan credits refresh each billing period and are priced for
                  recurring volume.
                </p>
              </div>
            )}

            <div
              className={cn(
                "flex flex-col items-center gap-3",
                showCreditPacks && "sm:items-end",
              )}
            >
              <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-sm">
                <button
                  onClick={() => setBillingInterval("month")}
                  className={cn(
                    "px-5 py-2 text-sm font-medium rounded-full transition-all",
                    !isYearly
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval("year")}
                  className={cn(
                    "px-5 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1.5",
                    isYearly
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  Yearly
                  <span
                    className={cn(
                      "px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none",
                      isYearly
                        ? "bg-emerald-400 text-white"
                        : "bg-emerald-50 text-emerald-600",
                    )}
                  >
                    Save ~20%
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "grid gap-5",
              planCardCount === 1
                ? "grid-cols-1"
                : planCardCount === 2
                  ? "grid-cols-1 md:grid-cols-2"
                  : planCardCount === 3
                    ? "grid-cols-1 md:grid-cols-3"
                    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
            )}
          >
            {availablePlansForUpgrade.map((plan) => {
              const displayPrice = getDisplayPrice(plan);
              const yearlyTotal = getYearlyTotal(plan);
              const isCurrentTier =
                !!currentPlanDetail &&
                comparePlanTier(plan, currentPlanDetail) === 0;
              const isPopular = plan.name === "Growth" && !isCurrentTier;
              const isCanceledPlan =
                canceledCheckout && canceledPriceId === plan.id;
              const isContinuationPlan =
                activeCheckoutContinuation?.priceId === plan.id;
              const isHighlightedPlan = isCanceledPlan || isContinuationPlan;

              return (
                <div
                  key={plan.id}
                  className={cn(
                    "relative rounded-2xl border bg-white p-6 transition-all",
                    isHighlightedPlan
                      ? "border-amber-300 ring-2 ring-amber-200 shadow-lg"
                      : isPopular
                        ? "border-slate-200 shadow-lg scale-[1.02]"
                        : "border-slate-200 hover:border-slate-300 hover:shadow-md",
                    isSinglePlanCard && singlePlanCardClass,
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm"
                        style={{ backgroundColor: "#FF5C35" }}
                      >
                        Recommended
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="space-y-1">
                      <h3 className="font-semibold text-lg text-slate-900">
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-slate-900">
                          ${displayPrice}
                        </span>
                        <span className="text-slate-400 text-sm">/mo</span>
                      </div>
                      {plan.interval === "year" && (
                        <p className="text-xs text-slate-400">
                          Billed annually at ${yearlyTotal}
                        </p>
                      )}
                    </div>

                    {isCurrentTier ? (
                      <Button
                        className="w-full mt-5 bg-white text-slate-500 border border-slate-200"
                        size="lg"
                        disabled
                      >
                        {plan.interval === currentPlanDetail?.interval
                          ? "Current plan"
                          : `Your plan, billed ${
                              currentPlanDetail?.interval === "year"
                                ? "yearly"
                                : "monthly"
                            }`}
                      </Button>
                    ) : (
                      <Button
                        className={cn(
                          "w-full mt-5",
                          isPopular
                            ? ""
                            : "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:text-slate-900",
                        )}
                        size="lg"
                        onClick={() =>
                          handlePlanCtaClick(plan, isHighlightedPlan)
                        }
                        disabled={isStartingCheckout}
                        aria-busy={startingCheckoutPriceId === plan.id}
                      >
                        {startingCheckoutPriceId === plan.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Starting checkout…
                          </>
                        ) : isHighlightedPlan ? (
                          `Continue with ${plan.name}`
                        ) : (
                          `Get ${plan.name}`
                        )}
                      </Button>
                    )}
                  </div>

                  <div
                    className={cn(
                      "mt-5 pt-5 border-t border-slate-100",
                      isSinglePlanCard && singlePlanFeaturesClass,
                    )}
                  >
                    <ul className="space-y-2.5">
                      {(plan.features || []).map(
                        (feature: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                            <span className="text-sm text-slate-600">
                              {feature}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              );
            })}

            {/* Business Card */}
            <div
              className={cn(
                "rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-md transition-all",
                isSinglePlanCard && singlePlanCardClass,
              )}
            >
              <div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg text-slate-900">
                    Business
                  </h3>
                  <p className="text-4xl font-bold text-slate-900">
                    Let&apos;s talk
                  </p>
                  <p className="text-xs text-slate-400">
                    Tailored to your needs
                  </p>
                </div>

                <Button
                  className="w-full mt-5 bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  size="lg"
                  asChild
                >
                  <a
                    href={BUSINESS_CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact us
                  </a>
                </Button>
              </div>

              <div
                className={cn(
                  "mt-5 pt-5 border-t border-slate-100",
                  isSinglePlanCard && singlePlanFeaturesClass,
                )}
              >
                <ul className="space-y-2.5">
                  {BUSINESS_FEATURES.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <p className="mt-5 max-w-2xl mx-auto text-center text-sm text-slate-500">
            Unused generated-post credits roll over up to 3x your monthly
            allowance.
          </p>
          <p className="mt-2 max-w-2xl mx-auto text-center text-sm text-slate-500">
            Still weighing your options?{" "}
            <a
              href="https://calendar.app.google/adcgznLQGj2DybGw6"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand hover:text-brand/80 hover:underline"
            >
              &rarr; Talk to the founder
            </a>
          </p>
        </section>

        {/* ── TESTIMONIALS ── */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-center text-slate-900 mb-6">
            Hear from our users
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-200 bg-white p-5"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-2.5">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                      {t.name}
                    </p>
                    <p className="text-xs text-slate-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-center text-slate-900 mb-6">
            Frequently asked questions
          </h2>
          <div className="space-y-2">
            {FAQ_ITEMS.map((item, index) => (
              <FaqItem
                key={index}
                question={item.question}
                answer={item.answer}
              />
            ))}
          </div>
        </div>

        {/* ── FOOTER TRUST ── */}
        <div className="mt-12 flex flex-col items-center gap-2 text-slate-400 text-xs">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              <span>7-day money back</span>
            </div>
            <span>|</span>
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg
              className="h-3.5 w-auto"
              viewBox="0 0 60 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="60" height="25" rx="4" fill="#635BFF" />
              <text
                x="30"
                y="17"
                textAnchor="middle"
                fill="white"
                fontSize="12"
                fontWeight="bold"
                fontFamily="system-ui, sans-serif"
              >
                Stripe
              </text>
            </svg>
            <span>Secure payments powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left hover:bg-slate-50 transition-colors">
        <span className="text-sm font-medium text-slate-700">{question}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-3 pt-2">
        <p className="text-sm text-slate-500 leading-relaxed">{answer}</p>
      </CollapsibleContent>
    </Collapsible>
  );
}
